import cx from "classnames";

import { Text } from "@/atoms";
import { useHaptics } from "@/modules/haptics/useHaptics/useHaptics";
import { formatCurrency } from "@/toolbox/Money";

const getDiscountLabel = (
  type: "percentage" | "amount" | "buyXGetY",
  discountPercentage: number,
  discountAmountInCents: number,
  packageName?: string
) => {
  if (type === "percentage") {
    return `${discountPercentage}% off`;
  }
  if (type === "amount") {
    return `${formatCurrency(discountAmountInCents, 0)} off`;
  }
  if (type === "buyXGetY" && packageName) {
    if (discountPercentage === 100) {
      return `+ Free ${packageName}`;
    }
    if (discountPercentage > 0) {
      return `+ ${discountPercentage}% off ${packageName}`;
    }
    if (discountAmountInCents > 0) {
      return `+ ${formatCurrency(discountAmountInCents, 0)} off ${packageName}`;
    }
  }
  return "";
};

type GiftCardAmountOptionProps = {
  amount: number;
  value: number;
  onSelect: (amount: number) => void;
  priceInformation?: {
    packageId: string;
    packagePriceId: string | null | undefined;
    originalPrice: number;
    price: number;
    discountedPrice: number;
    discountAmount: number;
    discountPercentage: number;
    isPercentage: boolean;
    isAmount: boolean;
    isBuyXGetY: boolean;
    packageName: string;
  };
};

export const GiftCardAmountOption = ({
  amount,
  value,
  onSelect,
  priceInformation,
}: GiftCardAmountOptionProps) => {
  const { triggerHaptic } = useHaptics();

  const handleSelect = () => {
    triggerHaptic("secondary");
    onSelect(amount);
  };

  return (
    <button
      type="button"
      className={cx(
        "flex h-20 flex-col items-center justify-center overflow-hidden rounded-lg bg-brand-color/[0.04] shadow-[0px_2px_4px_rgba(0,0,0,0.15)] transition-all",
        amount === value
          ? "ring-[3px] ring-brand-color"
          : "border border-gray-300"
      )}
      onClick={handleSelect}
    >
      <div className="flex flex-1 flex-col items-center justify-center">
        {/* First line */}
        <Text
          className="text-bold2 text-black"
          text={`${formatCurrency(amount, 0)} Gift Card`}
          variant="body1"
        />
        {/* Second line */}
        {priceInformation &&
          !priceInformation.isBuyXGetY &&
          (priceInformation.discountAmount > 0 ||
            priceInformation.discountPercentage > 0) &&
          priceInformation.discountedPrice < amount && (
            <div className="flex items-center gap-2 text-body1">
              <span className="text-gray-500 line-through">
                {formatCurrency(amount, 0)}
              </span>
              <span className="font-semibold text-green-600">
                {formatCurrency(
                  Math.max(priceInformation.discountedPrice, 0),
                  0
                )}
              </span>
            </div>
          )}
        {/* Second line Buy X Get Y */}
      </div>
      {priceInformation &&
        (priceInformation.discountAmount > 0 ||
          priceInformation.discountPercentage > 0) &&
        priceInformation.discountedPrice < amount && (
          <div className="rounded-b-inherit mt-auto w-full bg-brand-color/40 py-1 text-center text-chip font-semibold text-black">
            {getDiscountLabel(
              priceInformation.isBuyXGetY
                ? "buyXGetY"
                : priceInformation.isPercentage
                ? "percentage"
                : "amount",
              priceInformation.discountPercentage,
              priceInformation.discountAmount,
              priceInformation.packageName
            )}
          </div>
        )}
    </button>
  );
};
